import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";

interface TitleOptions {
  title?: string;
  searchValue?: string;
  onSearchChange?: (val: string) => void;
  searchPlaceholder?: string;
  action?: ReactNode;
}

interface TitleContextType {
  title: string;
  searchValue: string;
  onSearchChange?: (val: string) => void;
  searchPlaceholder: string;
  action?: ReactNode;
  setTitleOptions: (opts: TitleOptions) => void;
}

const TitleContext = createContext<TitleContextType>({
  title: "",
  searchValue: "",
  onSearchChange: undefined,
  searchPlaceholder: "Search...",
  action: undefined,
  setTitleOptions: () => {},
});

export function TitleProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<TitleOptions>({
    title: "",
    searchValue: "",
    onSearchChange: undefined,
    searchPlaceholder: "Search...",
    action: undefined,
  });

  const setTitleOptions = useCallback((newOpts: TitleOptions) => {
    setOptions((prev) => ({
      title: newOpts.title !== undefined ? newOpts.title : prev.title,
      searchValue: newOpts.searchValue !== undefined ? newOpts.searchValue : "",
      onSearchChange: newOpts.onSearchChange,
      searchPlaceholder: newOpts.searchPlaceholder || "Search...",
      action: newOpts.action,
    }));
  }, []);

  const value = useMemo(
    () => ({
      title: options.title || "",
      searchValue: options.searchValue || "",
      onSearchChange: options.onSearchChange,
      searchPlaceholder: options.searchPlaceholder || "Search...",
      action: options.action,
      setTitleOptions,
    }),
    [options, setTitleOptions],
  );

  return <TitleContext.Provider value={value}>{children}</TitleContext.Provider>;
}

export function usePageTitle(
  pageTitle?: string,
  opts?: {
    searchValue?: string;
    onSearchChange?: (val: string) => void;
    searchPlaceholder?: string;
    action?: ReactNode;
  },
) {
  const context = useContext(TitleContext);

  const searchValue = opts?.searchValue;
  const onSearchChange = opts?.onSearchChange;
  const searchPlaceholder = opts?.searchPlaceholder;
  const action = opts?.action;
  const setTitleOptions = context.setTitleOptions;

  useEffect(() => {
    setTitleOptions({
      title: pageTitle,
      searchValue: searchValue,
      onSearchChange: onSearchChange,
      searchPlaceholder: searchPlaceholder,
      action: action,
    });
  }, [pageTitle, searchValue, onSearchChange, searchPlaceholder, action, setTitleOptions]);

  return context;
}
